import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider.js";
import { BrandLogo } from "../ui/BrandLogo.js";

/**
 * Staff sign-in. No sign-up link — accounts are admin-created. First-time
 * users arrive with a temp password and are prompted to set a new one
 * (NEW_PASSWORD_REQUIRED), handled inline here.
 *
 * Three modes share this screen rather than three routes, because every path
 * between them is a straight line and the email field is common to all of
 * them: "credentials" → "request" (ask Cognito to mail a code) → "confirm"
 * (spend the code, choose a password) → back to "credentials" to use it.
 *
 * A staff account that is already in `RESET_REQUIRED` jumps straight to
 * "confirm": Cognito mailed a code the moment an admin reset it, so asking for
 * another one would only invalidate the one already in their inbox.
 */
type Mode = "credentials" | "request" | "confirm";

export function SignIn() {
  const { signIn, requestPasswordReset, confirmPasswordReset, status } = useAuth();
  const navigate = useNavigate();

  const [mode, setMode] = useState<Mode>("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [code, setCode] = useState("");
  const [needsNewPassword, setNeedsNewPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (status === "signed-in") return <Navigate to="/" replace />;
  if (status === "loading") return null;

  function goTo(next: Mode, message: string | null = null) {
    setMode(next);
    setError(null);
    setNotice(message);
    setPassword("");
    setNewPassword("");
    setCode("");
    setNeedsNewPassword(false);
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      if (mode === "request") {
        const { destination } = await requestPasswordReset(email);
        // Deliberately vague about whether the address has an account: the app
        // client sets `preventUserExistenceErrors`, and saying "we sent it to
        // <you>" here would leak what that setting exists to hide.
        goTo(
          "confirm",
          destination
            ? `If that address has an account, a code is on its way to ${destination}.`
            : "If that address has an account, a code is on its way to it.",
        );
        return;
      }

      if (mode === "confirm") {
        await confirmPasswordReset({ email, code, newPassword });
        goTo("credentials", "Password updated. Sign in with your new password.");
        return;
      }

      const { needsNewPassword: needs, needsPasswordReset } = await signIn({
        email,
        password,
        ...(needsNewPassword ? { newPassword } : {}),
      });
      if (needsPasswordReset) {
        goTo(
          "confirm",
          "This account's password was reset by an admin. Enter the code emailed to you and choose a new password.",
        );
        return;
      }
      if (needs) {
        setNeedsNewPassword(true);
        return;
      }
      navigate("/", { replace: true });
    } catch (err) {
      setError(authErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-dvh max-w-sm flex-col justify-center px-6">
      <BrandLogo className="mb-6" />
      <h1 className="text-2xl font-bold text-charcoal">Operations Console</h1>
      <p className="mt-1 text-sm text-charcoal">
        {mode === "credentials" ? "Staff sign-in" : "Reset your password"}
      </p>

      <form className="mt-8 space-y-4" onSubmit={onSubmit} noValidate>
        <Field label="Email">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            required
            // The code is bound to the address it was mailed to, so letting
            // them edit it mid-reset only produces a mismatch they can't read.
            readOnly={mode === "confirm"}
            className={mode === "confirm" ? readOnlyInputClass : inputClass}
          />
        </Field>

        {mode === "credentials" && (
          <Field label="Password">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              className={inputClass}
            />
          </Field>
        )}

        {mode === "confirm" && (
          <Field label="Verification code">
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              className={inputClass}
            />
          </Field>
        )}

        {(needsNewPassword || mode === "confirm") && (
          <div>
            <Field label={needsNewPassword ? "Set a new password" : "New password"}>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                required
                aria-describedby="password-rules"
                className={inputClass}
              />
            </Field>
            {/* Outside <Field>, because Field's <label> wraps its children: a
                hint nested in there lands in the input's accessible name and
                gets read out twice over, once as the name and once as the
                description this id points at. */}
            <p id="password-rules" className="mt-1 text-xs text-charcoal/70">
              At least 12 characters, with upper and lower case, a number, and a symbol.
            </p>
          </div>
        )}

        {notice && (
          <p role="status" className="text-sm text-charcoal">
            {notice}
          </p>
        )}

        {error && (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="inline-flex min-h-[44px] w-full items-center justify-center rounded-xl bg-brand-700 px-6 text-base font-semibold text-white shadow-sm active:bg-brand-800 disabled:opacity-60"
        >
          {submitLabel({ mode, submitting, needsNewPassword })}
        </button>
      </form>

      {mode === "credentials" ? (
        <button
          type="button"
          onClick={() => goTo("request")}
          className="mt-4 min-h-[44px] text-sm font-medium text-brand-700 underline underline-offset-2"
        >
          Forgot password?
        </button>
      ) : (
        <div className="mt-4 flex flex-col items-start gap-2">
          {mode === "confirm" && (
            <button
              type="button"
              onClick={() => goTo("request")}
              className="min-h-[44px] text-sm font-medium text-brand-700 underline underline-offset-2"
            >
              Send a new code
            </button>
          )}
          <button
            type="button"
            onClick={() => goTo("credentials")}
            className="min-h-[44px] text-sm font-medium text-brand-700 underline underline-offset-2"
          >
            Back to sign in
          </button>
        </div>
      )}
    </main>
  );
}

function submitLabel({
  mode,
  submitting,
  needsNewPassword,
}: {
  mode: Mode;
  submitting: boolean;
  needsNewPassword: boolean;
}) {
  if (mode === "request") return submitting ? "Sending…" : "Email me a code";
  if (mode === "confirm") return submitting ? "Updating…" : "Set new password";
  if (submitting) return "Signing in…";
  return needsNewPassword ? "Set password & sign in" : "Sign in";
}

/**
 * Cognito's own messages are written for developers ("Invalid code provided,
 * please request a code again"), and a couple of them describe an account
 * state the person on this screen cannot act on without being told who to ask.
 * Anything unrecognised falls through to its own text rather than a generic
 * apology — an unmapped error is still more useful than "something went wrong".
 */
export function authErrorMessage(err: unknown): string {
  const name = err && typeof err === "object" && "name" in err ? String(err.name) : "";
  switch (name) {
    case "CodeMismatchException":
      return "That code isn't right. Check the email and try again.";
    case "ExpiredCodeException":
      return "That code has expired. Ask for a new one.";
    case "LimitExceededException":
    case "TooManyRequestsException":
      return "Too many attempts. Wait a few minutes and try again.";
    case "InvalidPasswordException":
      return "That password doesn't meet the policy: at least 12 characters, with upper and lower case, a number, and a symbol.";
    case "NotAuthorizedException":
      return "That email and password don't match.";
    case "InvalidParameterException":
      // What Cognito returns for a reset against an account still holding a
      // temporary password — there is no password to recover yet, and the
      // remedy is the Team screen's "Resend invite", which an admin owns.
      return "This account hasn't been activated yet. Ask an admin to resend your invitation.";
    case "UserNotConfirmedException":
      return "This account hasn't been activated yet. Ask an admin to resend your invitation.";
    default:
      return err instanceof Error && err.message ? err.message : "Something went wrong. Try again.";
  }
}

const inputClass =
  "block w-full min-h-[44px] rounded-xl border border-charcoal/30 bg-white px-4 text-base text-charcoal shadow-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600";

const readOnlyInputClass = `${inputClass} bg-charcoal/5 text-charcoal/70`;

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-charcoal">{label}</span>
      {children}
    </label>
  );
}
